import type { Theme } from '@onefootprint/design-tokens';
import type { FootprintAppearanceVariables } from '@onefootprint/footprint-js';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';

import variablesMap from './constants/variables-map';

const createTheme = (baseTheme: Theme, variables?: FootprintAppearanceVariables): Theme => {
  const clonedBasedTheme = cloneDeep(baseTheme);
  if (!variables || Object.keys(variables).length === 0) {
    return clonedBasedTheme;
  }
  return Object.entries(variables).reduce(
    (theme, [tokenName, tokenValue]) => iterateOverVariables({ theme, variables, tokenName, tokenValue }),
    clonedBasedTheme,
  );
};

const mutateTheme = (options: { theme: Theme; key: string; value: string }) => {
  const { theme, key, value } = options;
  set(theme, key, value);
  return theme;
};

const iterateOverVariables = (options: {
  theme: Theme;
  variables: Record<string, unknown>;
  tokenName: string;
  tokenValue: unknown;
}) => {
  let { theme } = options;
  const { variables, tokenName, tokenValue } = options;
  const definitions = variablesMap.get(tokenName);
  if (definitions) {
    const key = definitions.var;
    theme = mutateTheme({
      theme,
      key,
      value: tokenValue as string,
    });
    if (definitions.assignDefault) {
      definitions.assignDefault.forEach(innerTokenName => {
        const shouldOverwrite = !(innerTokenName in variables);
        if (shouldOverwrite) {
          theme = iterateOverVariables({
            theme,
            variables,
            tokenName: innerTokenName,
            tokenValue,
          });
        }
      });
    }
    return theme;
  }
  return theme;
};

// Family is ignored: input, hint, and label inherit `font-family`.
const parseFontShorthand = (typography: string) => {
  const parts = typography.trim().split(/\s+/);
  const sizePattern = /^(\d+(?:\.\d+)?(?:px|em|rem|%|pt|vh|vw))(?:\/(.+))?$/;
  let fontSize: string | undefined;
  let lineHeight: string | undefined;
  let fontWeight: string | number = 400;

  for (const part of parts) {
    const sizeMatch = part.match(sizePattern);
    if (sizeMatch) {
      fontSize = sizeMatch[1];
      lineHeight = sizeMatch[2];
      break;
    }
    if (/^[1-9]00$/.test(part)) {
      fontWeight = Number.parseInt(part, 10);
    } else if (['bold', 'bolder', 'lighter', 'normal'].includes(part.toLowerCase())) {
      fontWeight = part.toLowerCase();
    }
  }

  if (!fontSize) {
    return;
  }

  return { fontWeight, fontSize, lineHeight };
};

const typographyFromValue = (cssVar: string, value: unknown) => {
  if (typeof value === 'object' && value !== null) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string' && cssVar.endsWith('-font')) {
    return parseFontShorthand(value);
  }
  return;
};

const createCssVariables = (theme: Theme): string | undefined => {
  const cssVariables: string[] = [];

  variablesMap.forEach(definitions => {
    if (!definitions.cssVar) {
      return;
    }
    const value = get(theme, definitions.var);
    if (!value) {
      return;
    }
    // Longhands, not `font:` — one missing part in a shorthand invalidates size, weight
    // and line-height together.
    const typography = typographyFromValue(definitions.cssVar, value);
    if (typography) {
      if (typography.fontWeight) {
        cssVariables.push(`  --${definitions.cssVar}-weight: ${typography.fontWeight};`);
      }
      if (typography.fontSize) {
        cssVariables.push(`  --${definitions.cssVar}-size: ${typography.fontSize};`);
      }
      if (typography.lineHeight) {
        cssVariables.push(`  --${definitions.cssVar}-line-height: ${typography.lineHeight};`);
      }
      return;
    }
    if (definitions.cssVar.endsWith('-font')) {
      return;
    }
    cssVariables.push(`  --${definitions.cssVar}: ${String(value).replace(/;\s*$/, '')};`);
  });

  if (cssVariables.length === 0) {
    return;
  }

  // `.dark` is on `body`. Component variables redeclared under `:root .dark` win over
  // inherited `:root` values, so customer overrides have to land on that scope too.
  return `:root, :root .dark {\n${cssVariables.join('\n')}\n}`;
};

export { createCssVariables, createTheme };
export default createTheme;
